import {
  useAdminMessagePreview,
  useAdminMessages,
  useSetAdminMessageEnabled,
  type AdminMessage,
  type AdminMessagePreview,
  type AdminMessageScope,
  type AdminMessageStatus,
} from '@fidalia/api';
import {
  Alert,
  ApercuMessage,
  Avatar,
  Badge,
  BadgeCategorie,
  Button,
  DataTable,
  Drawer,
  IconBadge,
  Modal,
  MotifsChip,
  Input,
  NOMS_CATEGORIE_COMPLETS,
  nomCategorieComplet,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Switch,
  type TableColumn,
} from '@fidalia/ui';
import { createFileRoute, Link } from '@tanstack/react-router';

import logoDoctolib from '@/assets/doctolib-logomark.png';
import { useState } from 'react';
import {
  MdBlock,
  MdMarkEmailRead,
  MdPlayArrow,
  MdSchedule,
  MdSend,
  MdSearch,
  MdTune,
  MdVisibility,
} from 'react-icons/md';

import { errorMessage } from '@/lib/auth';
import { useDebounced } from '@/lib/use-debounced';

const PAGE_SIZE = 50;

export const Route = createFileRoute('/_app/messages')({
  component: MessagesPage,
});

/** « 2 j avant », « 3 h après » : le décalage est en heures, on le lit en jours quand il tombe juste. */
function decalage(message: AdminMessage, { parentheses = true } = {}): string {
  const { offsetHours: heures, timing, sendHour } = message;
  const duree = heures >= 24 && heures % 24 === 0 ? `${heures / 24} j` : `${heures} h`;
  const sens = timing === 'before' ? 'avant' : 'après';
  const heure = sendHour !== null ? (parentheses ? ` (à ${sendHour} h)` : `, à ${sendHour} h`) : '';
  return `${duree} ${sens}${heure}`;
}

function colonnes({
  onApercu,
  onBascule,
}: {
  onApercu: (message: AdminMessage) => void;
  onBascule: (message: AdminMessage) => void;
}): TableColumn<AdminMessage>[] {
  return [
    {
      key: 'cabinet',
      title: 'Cabinet',
      cell: (m) => (
        <div className="flex items-center gap-3">
          <Avatar name={m.cabinetNom} size="sm" tone="auto" />
          <div className="flex min-w-0 flex-col items-start">
            {m.cabinetUserId ? (
              <Link
                to="/users/$id"
                params={{ id: m.cabinetUserId }}
                className="font-medium text-brand-600 hover:underline"
              >
                {m.cabinetNom}
              </Link>
            ) : (
              <span className="font-medium">{m.cabinetNom}</span>
            )}
            {m.cabinetEmail ? (
              <span className="truncate text-xs text-content-muted">{m.cabinetEmail}</span>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      key: 'categorie',
      title: 'Catégorie',
      cell: (m) => <BadgeCategorie predefiniId={m.catalogKey} complet />,
    },
    {
      key: 'message',
      title: 'Message',
      className: 'max-w-sm whitespace-normal',
      cell: (m) => (
        <div className="flex flex-col">
          <span className="font-medium">{m.name}</span>
          <span className="truncate text-xs text-content-muted" title={m.subject}>
            {m.subject}
          </span>
        </div>
      ),
    },
    {
      key: 'cible',
      title: 'Motifs',
      cell: (m) =>
        m.fallback ? (
          <Badge tone="neutral" size="sm">
            Par défaut
          </Badge>
        ) : m.motifsListe.length > 0 ? (
          <MotifsChip motifs={m.motifsListe} logo={logoDoctolib} align="start" appearance="badge" />
        ) : (
          <Badge tone="brand" size="sm">
            Tous les motifs
          </Badge>
        ),
    },
    {
      key: 'sent',
      title: 'Envoyés',
      align: 'right',
      cell: (m) => <span className="tabular-nums">{m.sent}</span>,
    },
    {
      key: 'statut',
      title: 'Statut',
      cell: (m) => (
        // Le clic n'applique rien : il ouvre la confirmation (voir `MessagesPage`).
        <Switch
          label={`Message « ${m.name} » du cabinet ${m.cabinetNom}`}
          hideLabel
          checked={m.enabled}
          onChange={() => onBascule(m)}
        />
      ),
    },
    {
      key: 'apercu',
      align: 'right',
      cell: (m) => (
        <Button
          variant="outline"
          size="sm"
          icon={<MdVisibility className="size-4" />}
          onClick={() => onApercu(m)}
        >
          Aperçu
        </Button>
      ),
    },
  ];
}

/** Confirmation avant d'allumer ou d'éteindre le message d'un cabinet : il part à de vrais patients. */
function ConfirmationBascule({
  message,
  onClose,
}: {
  message: AdminMessage | null;
  onClose: () => void;
}) {
  const bascule = useSetAdminMessageEnabled();
  // Dernier message ouvert : la boîte se ferme avec `message = null` et ne doit pas
  // perdre son texte pendant l'animation de sortie.
  const [dernier, setDernier] = useState<AdminMessage | null>(null);
  if (message && message !== dernier) setDernier(message);
  const cible = message ?? dernier;
  const activer = cible ? !cible.enabled : false;

  const fermer = () => {
    bascule.reset();
    onClose();
  };

  return (
    <Modal
      open={message !== null}
      onClose={fermer}
      size="md"
      icon={
        <IconBadge tone={activer ? 'success' : 'danger'} size="md">
          {activer ? <MdPlayArrow /> : <MdBlock />}
        </IconBadge>
      }
      title={activer ? 'Activer ce message ?' : 'Désactiver ce message ?'}
      description={
        cible ? (
          <>
            Êtes-vous sûr de vouloir {activer ? 'activer' : 'désactiver'} le message{' '}
            <strong>{nomCategorieComplet(cible.catalogKey)}</strong> «&nbsp;{cible.name}&nbsp;» pour
            le cabinet <strong>{cible.cabinetNom}</strong> ?{' '}
            {activer
              ? 'Ses patients le recevront automatiquement.'
              : 'Plus aucun envoi automatique ne partira pour ce message.'}
          </>
        ) : null
      }
      footer={
        <div className="flex flex-col gap-3">
          {bascule.error ? (
            <Alert tone="danger">{errorMessage(bascule.error, 'Modification impossible.')}</Alert>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={fermer}>
              Annuler
            </Button>
            <Button
              variant={activer ? 'primary' : 'danger'}
              loading={bascule.isPending}
              onClick={() => {
                if (!cible) return;
                bascule.mutate({ id: cible.id, enabled: activer }, { onSuccess: fermer });
              }}
            >
              {activer ? 'Activer' : 'Désactiver'}
            </Button>
          </div>
        </div>
      }
    />
  );
}

const DATE_COURTE = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/**
 * L'en-tête du panneau, sur quatre lignes : le nom et l'objet, le cabinet, puis
 * toutes les pastilles à la suite (catégorie, état, moment d'envoi, volume,
 * motifs) et la date de dernière modification.
 */
function EnteteApercu({
  message,
  preview,
}: {
  message: AdminMessage;
  preview: AdminMessagePreview | undefined;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex min-w-0 flex-col">
        <h3 className="truncate text-lg font-semibold text-content">{message.name}</h3>
        <p className="text-xs text-content-muted">
          Modifié le {DATE_COURTE.format(new Date(message.updatedAt))}
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-field border border-border-subtle bg-surface p-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <BadgeCategorie predefiniId={message.catalogKey} complet />
          <Badge tone="neutral" size="sm" icon={<MdSchedule className="size-3.5" />}>
            {decalage(message)}
          </Badge>
          <Badge tone="neutral" size="sm" icon={<MdSend className="size-3.5" />}>
            {message.sent} envoi{message.sent > 1 ? 's' : ''}
          </Badge>
        </div>

        <p className="flex flex-wrap items-center gap-1.5 text-sm text-content-muted">
          Se déclenche pour
          {preview && preview.motifs.length > 0 ? (
            <MotifsChip
              motifs={preview.motifs}
              logo={logoDoctolib}
              align="start"
              appearance="link"
            />
          ) : message.fallback ? (
            <span className="text-content">les rendez-vous sans autre message</span>
          ) : (
            <span className="text-content">tous les motifs</span>
          )}
        </p>
      </div>
    </div>
  );
}

/** Le modèle dans un téléphone, tel que le patient le recevra. */
function ApercuDrawer({ message, onClose }: { message: AdminMessage | null; onClose: () => void }) {
  const { data, error } = useAdminMessagePreview(message?.id ?? null);

  return (
    <Drawer
      open={message !== null}
      onClose={onClose}
      title={message?.name}
      header={message ? <EnteteApercu message={message} preview={data} /> : null}
    >
      {error ? (
        <Alert tone="danger">{errorMessage(error, 'Aperçu indisponible.')}</Alert>
      ) : !data ? (
        <Skeleton className="mx-auto h-[600px] w-full max-w-[300px] rounded-[2.6rem]" />
      ) : (
        <ApercuMessage
          titre="Aperçu email"
          expediteur={data.sender}
          cabinet={data.cabinet}
          logoUrl={data.logoUrl}
          objet={data.subject}
          corps={data.body}
          signature={data.signature.body}
          logoSignature={data.signature.withLogo}
          bouton={data.hasReviewButton ? { label: 'Donner mon avis' } : undefined}
        />
      )}
    </Drawer>
  );
}

function MessagesPage() {
  const [apercu, setApercu] = useState<AdminMessage | null>(null);
  const [aBasculer, setABasculer] = useState<AdminMessage | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [catalogKey, setCatalogKey] = useState('');
  const [scope, setScope] = useState<AdminMessageScope>('');
  // Un filet de sécurité éteint est créé pour chaque cabinet à la première ouverture de
  // l'écran : sans filtre, la liste serait surtout faite de messages que personne n'a
  // allumés. Par défaut on ne montre donc que ce qui est activé.
  const [status, setStatus] = useState<AdminMessageStatus>('enabled');

  /** Tout changement de filtre repart de la première page. */
  const filtrer =
    <T,>(setter: (valeur: T) => void) =>
    (valeur: T) => {
      setter(valeur);
      setPage(1);
    };

  const { data, error } = useAdminMessages({
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch,
    catalogKey,
    scope,
    status,
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdMarkEmailRead />}
        title="Messages"
        description="Les messages automatiques que les cabinets ont mis en route : catégorie, moment d’envoi, motifs visés et volume envoyé."
      />

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          type="search"
          aria-label="Rechercher un message"
          placeholder="Cabinet, nom, objet…"
          icon={<MdSearch className="size-6" />}
          containerClassName="h-11 w-full sm:min-w-64 sm:flex-1"
          value={search}
          onChange={(event) => filtrer(setSearch)(event.target.value)}
        />

        <Select
          aria-label="Filtrer par catégorie"
          containerClassName="h-11 w-full sm:w-48"
          value={catalogKey}
          onChange={(event) => filtrer(setCatalogKey)(event.target.value)}
        >
          <option value="">Toutes catégories</option>
          {Object.entries(NOMS_CATEGORIE_COMPLETS).map(([cle, nom]) => (
            <option key={cle} value={cle}>
              {nom}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Filtrer par cible"
          icon={<MdTune className="size-5" />}
          containerClassName="h-11 w-full sm:w-52"
          value={scope}
          onChange={(event) => filtrer(setScope)(event.target.value as AdminMessageScope)}
        >
          <option value="">Toutes cibles</option>
          <option value="fallback">Par défaut</option>
          <option value="targeted">Ciblés sur des motifs</option>
        </Select>

        <Select
          aria-label="Filtrer par statut"
          containerClassName="h-11 w-full sm:w-44"
          value={status}
          onChange={(event) => filtrer(setStatus)(event.target.value as AdminMessageStatus)}
        >
          <option value="">Tous les statuts</option>
          <option value="enabled">Activés</option>
          <option value="disabled">Désactivés</option>
        </Select>

        {data ? (
          <p className="text-sm text-content-muted sm:ml-auto">
            <span className="font-semibold text-content">{data.total}</span> message
            {data.total > 1 ? 's' : ''}
          </p>
        ) : null}
      </div>

      <DataTable
        mobileCards
        data={data?.items ?? []}
        columns={colonnes({ onApercu: setApercu, onBascule: setABasculer })}
        isLoading={!data}
        skeletonRows={10}
        rowId={(m) => m.id}
        emptyMessage={
          debouncedSearch ? `Aucun résultat pour « ${debouncedSearch} ».` : 'Aucun message.'
        }
      />

      {data ? (
        <Pagination
          page={page}
          total={data.total}
          limit={PAGE_SIZE}
          onChange={setPage}
          label="messages"
        />
      ) : null}

      <ApercuDrawer message={apercu} onClose={() => setApercu(null)} />
      <ConfirmationBascule message={aBasculer} onClose={() => setABasculer(null)} />
    </div>
  );
}
